<template>
  <div class="cataloguer-form-v2">
    <div v-if="!searchContext" class="d-flex align-items-center mb-2" style="gap: 8px;">
      <div class="flex-grow-1 d-flex justify-content-center">
        <div style="width: 100%; max-width: 420px; position: relative;">
          <i class="pi pi-search" style="position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: #6c757d; z-index: 1;"></i>
          <input
            v-model="itemSearch"
            type="text"
            class="form-control form-control-sm"
            placeholder="Search specification, mat. number, or item code..."
            style="padding-left: 34px;"
          />
        </div>
      </div>
    </div>

    <!-- Tambah item (tombol Add di toolbar New_McrItem -> addNewItem). Material Number cuma ditanya untuk MCR002. -->
    <AddItemModal
      v-model:visible="addItemModal.visible"
      :mcr-type="mcrType"
      :required-plant="requiredItemPlant"
      :on-loading="addItemModal.onLoading"
      @submit="addItem"
    />

    <McrItemsTable
      v-model:pagination="mainTable.page"
      :items="filteredMcrItems"
      :loading="mainTable.onLoad || listLoading"
      :loading-more="loadingMore"
      :skeleton="mainTable.data"
      :total="searchContext ? stateAllMcrItem.length : mainTable.total"
      :columns="columns"
      :selected="tableSelectedItem"
      :show-actions="itemDeletable"
      :check-duplicate-visible="hasDuplicateSource"
      :cancel-visible="cancelVisible"
      @update:selected="handleTableSelection"
      @delete="deleteItem"
      @note-click="openNoteModal"
      @check-duplicate-click="openCheckDuplicateDialog"
      @cancel-click="onCancelItem"
      @load-more="$emit('load-more')"
    />

    <!-- Modal tombol MCR Item Note / Check Duplicate (handler-nya dari New_mcrItemActionsMixin) -->
    <AddNoteItemModal v-model:visible="noteModal.visible" :item="noteModal.item" @saved="onNotesSaved" />
    <CheckDuplicateModal
      v-model:visible="checkDuplicateModal.visible"
      :mcr-item="checkDuplicateModal.item"
      :row-number="checkDuplicateModal.rowNumber"
      @saved="onStatusSaved"
    />

    <div class="tabs-container">
      <Tabs v-model:value="activeTab" scrollable>
        <TabList>
          <Tab v-for="tab in filteredTabList" :key="tab.value" :value="tab.value">
            {{ tab.label }}
            <!-- Error Save yang masih aktif di tab ini (hilang begitu field/kolom yang error terisi, sama dengan garis merahnya) -->
            <span v-if="tabErrorFlags[tab.configKey]" v-tooltip.top="'Some fields in this tab still need attention'" class="exclamation-mark-error-tab">!</span>
          </Tab>
        </TabList>

        <div v-if="tabLoading" class="tab-skeleton-overlay">
          <div class="row g-3 pt-2">
            <div class="col-md-6">
              <div class="card h-100">
                <div class="card-header pt-3 pb-2 px-3">
                  <Skeleton width="140px" height="16px" />
                </div>
                <div class="card-body pt-0 px-3">
                  <div v-for="i in 6" :key="i" class="row g-2 mt-1">
                    <div class="col-md-6"><Skeleton height="14px" class="mb-1" /><Skeleton height="32px" /></div>
                    <div class="col-md-6"><Skeleton height="14px" class="mb-1" /><Skeleton height="32px" /></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else-if="!selectedMcrItem" class="text-muted text-center small py-4">
          Select an item from the table above.
        </div>

        <!-- Tab hasil clone (New_*) sengaja dirender TANPA props dulu -- props ditambahkan per tab saat
             field-nya dimigrasikan ke FORM02. Pilot yang sudah pakai FORM02: New_BasicDataTab & New_PlantStorageTab. -->
        <TabPanels v-show="!tabLoading && selectedMcrItem" :class="{ 'form-disabled': readonly }">
          <TabPanel value="6">
            <UserRequestTab v-if="activatedTabs['6'] && selectedMcrItem" :mcr-item="selectedMcrItem" :mcr-type="mcrType" />
          </TabPanel>
          <TabPanel value="0">
            <BasicDataTab
              v-if="activatedTabs['0'] && selectedMcrItem"
              :mcr-item="selectedMcrItem"
              :set-error-valid-cat="setErrorValidCat"
              :selected-mcr-item-index="selectedMcrItemIndex"
              :disabled="readonly"
              :form-config="formConfig"
              :mcr-type="mcrType"
              :mcr-status="mcrStatus"
            />
          </TabPanel>
          <TabPanel value="15">
            <PoTextTab
              v-if="activatedTabs['15'] && selectedMcrItem"
              :mcr-item="selectedMcrItem"
              :set-error-valid-cat="setErrorValidCat"
              :selected-mcr-item-index="selectedMcrItemIndex"
              :disabled="readonly"
              :form-config="formConfig"
              :mcr-type="mcrType"
              :mcr-status="mcrStatus"
            />
          </TabPanel>
          <TabPanel value="16">
            <AttachmentKeywordTab
              v-if="activatedTabs['16'] && selectedMcrItem"
              :mcr-item="selectedMcrItem"
              :set-error-valid-cat="setErrorValidCat"
              :selected-mcr-item-index="selectedMcrItemIndex"
              :disabled="readonly"
              :form-config="formConfig"
              :mcr-type="mcrType"
              :mcr-status="mcrStatus"
            />
          </TabPanel>
          <TabPanel value="2">
            <ManufactureEquipmentTab
              v-if="activatedTabs['2'] && selectedMcrItem"
              :mcr-item="selectedMcrItem"
              :set-error-valid-cat="setErrorValidCat"
              :selected-mcr-item-index="selectedMcrItemIndex"
              :disabled="readonly"
              :form-config="formConfig"
              :mcr-type="mcrType"
              :mcr-status="mcrStatus"
            />
          </TabPanel>
          <TabPanel value="17">
            <PlantStorageTab
              v-if="activatedTabs['17'] && selectedMcrItem"
              :mcr-item="selectedMcrItem"
              :disabled="readonly"
              :form-config="formConfig"
              :mcr-type="mcrType"
              :mcr-status="mcrStatus"
            />
          </TabPanel>
          <TabPanel value="5">
            <AccountingTab
              v-if="activatedTabs['5'] && selectedMcrItem"
              :mcr-item="selectedMcrItem"
              :set-error-valid-cat="setErrorValidCat"
              :selected-mcr-item-index="selectedMcrItemIndex"
              :disabled="readonly"
              :form-config="formConfig"
              :mcr-type="mcrType"
              :mcr-status="mcrStatus"
            />
          </TabPanel>
          <TabPanel value="1">
            <MrpTab
              v-if="activatedTabs['1'] && selectedMcrItem"
              :mcr-item="selectedMcrItem"
              :set-error-valid-cat="setErrorValidCat"
              :selected-mcr-item-index="selectedMcrItemIndex"
              :disabled="readonly"
              :form-config="formConfig"
              :mcr-type="mcrType"
              :mcr-status="mcrStatus"
            />
          </TabPanel>
          <TabPanel value="18">
            <PurchasingQualManagement
              v-if="activatedTabs['18'] && selectedMcrItem"
              :mcr-item="selectedMcrItem"
              :set-error-valid-cat="setErrorValidCat"
              :selected-mcr-item-index="selectedMcrItemIndex"
              :disabled="readonly"
              :form-config="formConfig"
              :mcr-type="mcrType"
              :mcr-status="mcrStatus"
            />
          </TabPanel>
          <TabPanel value="10">
            <SalesGeneralSales
              v-if="activatedTabs['10'] && selectedMcrItem"
              :mcr-item="selectedMcrItem"
              :set-error-valid-cat="setErrorValidCat"
              :selected-mcr-item-index="selectedMcrItemIndex"
              :disabled="readonly"
              :form-config="formConfig"
              :mcr-type="mcrType"
              :mcr-status="mcrStatus"
            />
          </TabPanel>
          <TabPanel value="12">
            <WarehouseManagementTab
              v-if="activatedTabs['12'] && selectedMcrItem"
              :mcr-item="selectedMcrItem"
              :set-error-valid-cat="setErrorValidCat"
              :selected-mcr-item-index="selectedMcrItemIndex"
              :disabled="readonly"
              :form-config="formConfig"
              :mcr-type="mcrType"
              :mcr-status="mcrStatus"
            />
          </TabPanel>
          <TabPanel value="19">
            <DataSatuInspectionTextTab
              v-if="activatedTabs['19'] && selectedMcrItem"
              :mcr-item="selectedMcrItem"
              :set-error-valid-cat="setErrorValidCat"
              :selected-mcr-item-index="selectedMcrItemIndex"
              :disabled="readonly"
              :form-config="formConfig"
              :mcr-type="mcrType"
              :mcr-status="mcrStatus"
            />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </div>
  </div>
</template>

<script>
import { getCurrentInstance } from "vue";
import { mapState, mapActions } from "vuex";
import api from "@/helpers/api";
import materialNumberFormatter from "@/helpers/materialNumberFormatter";
import requestListService from "@/services/materialCatalogingRequest/requestListService";
import advanceSearchService from "@/services/generalLayout/advanceSearchService";
import serviceAdvanceService from "@/services/generalLayout/serviceAdvanceService";
import userFormService from "@/services/materialCatalogingRequest/userFormService";
import { useFormConfigEngine } from "@/composables/useFormConfigEngine";
import McrItemsTable from "./McrItemsTable.vue";
import UserRequestTab from "./modules/cataloguer/New_UserRequestTab.vue";
import BasicDataTab from "./modules/cataloguer/New_BasicDataTab.vue";
import PoTextTab from "./modules/cataloguer/New_PoTextTab.vue";
import ManufactureEquipmentTab from "./modules/cataloguer/New_ManufactureEquipmentTab.vue";
import PlantStorageTab from "./modules/cataloguer/New_PlantStorageTab.vue";
import AccountingTab from "./modules/cataloguer/New_AccountingTab.vue";
import MrpTab from "./modules/cataloguer/New_MrpTab.vue";
import PurchasingQualManagement from "./modules/cataloguer/New_PurchasingQualManagement.vue";
import SalesGeneralSales from "./modules/cataloguer/New_SalesGeneralSales.vue";
import WarehouseManagementTab from "./modules/cataloguer/New_WarehouseManagementTab.vue";
import AttachmentKeywordTab from "./modules/cataloguer/New_AttachmentKeywordTab.vue";
import DataSatuInspectionTextTab from "./modules/cataloguer/New_DataSatuInspectionTextTab.vue";
import AddItemModal from "./modules/cataloguer/New_AddItemModal.vue";
import AddNoteItemModal from "./modules/cataloguer/AddNoteItemModal.vue";
import CheckDuplicateModal from "./modules/cataloguer/CheckDuplicateModal.vue";
import mcrItemActionsMixin from "./modules/New_mcrItemActionsMixin";
import { CATALOGUER_TAB_LIST } from "./modules/cataloguer/cataloguerFormConfig";
import formConfigHelper from "@/helpers/New_formConfig";

// Daftar tab (`configKey` = key tab di FORM02_BASE.json) ada di cataloguerFormConfig.js -- dipakai juga label
// modal validasi di McrFooter. Tab yang belum punya entry di config tetap tampil (sama seperti
// filteredTabList di CataloguerForm.vue); yang sudah punya, tampil/tidaknya dari isTabVisibleFn (per mcr_type).
const TAB_LIST = CATALOGUER_TAB_LIST;

// Endpoint detail & key identitas baris per item_source. Pemanggilan service API (bukan aturan bisnis), jadi
// TETAP di kode -- yang diatur dari config cuma item_source-nya (meta route halaman search) & pemetaan field (hydrate_from).
const ITEM_SOURCES = {
  emcr_item: { key: "mcr_item_code", fetchDetail: (item) => requestListService.detailEmcrItem({ mcr_item_code: item.mcr_item_code }) },
  eu_material: { key: "material_number", fetchDetail: (item) => advanceSearchService.getMaterialDetail(item.material_number) },
  eu_service: { key: "service_number", fetchDetail: (item) => serviceAdvanceService.getServiceDetail(item.service_number) }
};


export default {
  name: "CataloguerForm",
  components: {
    McrItemsTable,
    UserRequestTab,
    BasicDataTab,
    PoTextTab,
    ManufactureEquipmentTab,
    PlantStorageTab,
    AccountingTab,
    MrpTab,
    PurchasingQualManagement,
    SalesGeneralSales,
    WarehouseManagementTab,
    AttachmentKeywordTab,
    DataSatuInspectionTextTab,
    AddItemModal,
    AddNoteItemModal,
    CheckDuplicateModal
  },
  // Tombol MCR Item Note / Check Duplicate / Cancel di McrItemsTable: modal & handler dari mixin, aturan tampilnya
  // (hasDuplicateSource/cancelVisible, diwarisi CataloguerForm.vue) di methods form ini.
  mixins: [mcrItemActionsMixin],
  props: {
    readonly: { type: Boolean, default: false },
    // Tombol Delete per item di tabel item (kolom Action McrItemsTable). Syaratnya = tombol Add toolbar New_McrItem
    // (`showItemActions`: route draft/my-task, ANTAM MCR001/MCR002, role User / Inventory Control, MCR draft).
    // Halaman search tidak mengirim prop ini -> tanpa tombol Delete.
    itemDeletable: { type: Boolean, default: false },
    // Context halaman search dari meta route (New_searchPageMixin) ({ company, route, item_source,
    // form_code, mcr_type }), dikirim halaman search. Jalur MCR (my-task/edit-sync-task/material-request) TIDAK
    // mengirim prop ini -> item_source "emcr_item" & mcr_type dari header MCR. Lewat prop (bukan store) supaya
    // tidak ada sisa context search saat user pindah ke halaman MCR.
    searchContext: { type: Object, default: null },
    // Halaman search: kolom tabel (layout per halaman), status loading daftar & infinite scroll milik halaman search.
    searchColumns: { type: Array, default: null },
    listLoading: { type: Boolean, default: false },
    loadingMore: { type: Boolean, default: false }
  },
  emits: ["load-more"],
  setup() {
    // Di level form cuma butuh evaluator tab (isTabVisibleFn) -- ctx-nya dari item yang sedang dipilih.
    const vm = getCurrentInstance().proxy;
    const { isTabVisibleFn, formConfigCtx, resolveSourcePath } = useFormConfigEngine({
      vm,
      getFormConfig: () => vm.formConfig,
      getDialog: () => ({ form: vm.selectedMcrItem || {}, options: {}, loading: {}, loaded: {}, errors: {} }),
      getMcrType: () => vm.mcrType,
      fieldResolvers: { item_source: () => vm.itemSource, mcr_status: () => vm.mcrStatus }
    });
    return { isTabVisibleFn, formConfigCtx, resolveSourcePath };
  },
  data() {
    return {
      activeTab: "0",
      itemSearch: "",
      activatedTabs: { "0": true },
      tabLoading: false,
      tableSelectedItem: null,
      addItemModal: {
        visible: false,
        onLoading: false
      },
      mainTable: {
        data: Array.from({ length: 10 }, () => ({})),
        onLoad: false,
        total: 0,
        page: { current_page: 1, total_page: 1, per_page: 10 }
      }
    };
  },
  computed: {
    ...mapState({
      mcrHeader: (state) => state.mcr.mcrHeader,
      mcrDetail: (state) => state.mcr.mcrDetail,
      stateAllMcrItem: (state) => state.mcr.allMcrItem || [],
      // Item terpilih = SATU objek di store (bukan salinan lokal): yang ditulis tab New_* lewat prop mcrItem & dibaca
      // New_McrFooter.saveCatalog. mutation setAllMcrItem menjaganya tetap elemen allMcrItem.
      selectedMcrItem: (state) => state.mcr.selectedMcrItem,
      setErrorValidCat: (state) => state.mcr.setErrorValidCat || []
    }),
    // Config FORM02 hasil resolve BE (BASE + overlay company), dimuat New_McrForm lewat mcr/fetchFormConfig.
    formConfig() {
      return this.mcrDetail?.formConfig || null;
    },
    // Sumber data item -- dibaca `when` di hydrate_from. Halaman search: dari meta route (prop
    // searchContext); jalur MCR: emcr_item. Tidak ada pencocokan nama route/path di sini.
    itemSource() {
      return this.searchContext?.item_source || "emcr_item";
    },
    // Halaman search: mcr_type ditetapkan di meta route (response search tidak punya mcr_type).
    // Jalur MCR: dari header MCR. Header store SENGAJA tidak dibaca di halaman search -- bisa berisi sisa MCR
    // yang dibuka sebelumnya. Dikirim ke tab lewat prop `mcr-type` supaya aturan ini cuma ada di sini.
    mcrType() {
      if (this.searchContext) return this.searchContext.mcr_type || "";
      return this.mcrHeader?.type || "";
    },
    // Status header MCR (lowercase, mis. "draft") -- context `mcr_status` (ANTAM draft vs non-draft). Halaman search tidak
    // punya header MCR -> kosong (header store bisa sisa MCR sebelumnya, sama alasannya dengan mcrType).
    // Aturan ANTAM (MCR001/MCR002, MCR masih draft): semua item dalam satu MCR wajib memakai plant yang SAMA -- item baru
    // (Add Item) harus memakai plant item yang sudah ada. null = tidak dibatasi (item pertama / company & kondisi lain).
    requiredItemPlant() {
      if (this.searchContext) return null;
      const company = String(this.mcrHeader?.company || "").toUpperCase();
      if (company !== "ANTAM" || !["MCR001", "MCR002"].includes(this.mcrType) || this.mcrStatus !== "draft") return null;
      const existing = this.stateAllMcrItem.find((item) => item?.cat_plant || item?.plant);
      return existing ? (existing.cat_plant || existing.plant) : null;
    },
    mcrStatus() {
      if (this.searchContext) return "";
      return String(this.mcrHeader?.status || "").toLowerCase();
    },
    filteredTabList() {
      const tabFields = this.formConfig?.tab_fields;
      if (!tabFields) return TAB_LIST;
      return TAB_LIST.filter((tab) => !tabFields[tab.configKey] || this.isTabVisibleFn(tab.configKey));
    },
    // { configKey tab: true } untuk tab yang masih punya error Save aktif pada item terpilih (aturan: formConfigHelper.hasActiveTabError).
    tabErrorFlags() {
      const errors = this.setErrorValidCat?.[this.selectedMcrItemIndex];
      if (!errors || !this.selectedMcrItem || !this.formConfig) return {};
      return Object.fromEntries(TAB_LIST.filter((tab) => tab.configKey).map((tab) => [
        tab.configKey,
        formConfigHelper.hasActiveTabError(this.formConfig, tab.configKey, this.selectedMcrItem, errors, this.formConfigCtx)
      ]));
    },
    selectedMcrItemIndex() {
      if (!this.selectedMcrItem?.id) return -1;
      return this.stateAllMcrItem.findIndex((item) => item.id === this.selectedMcrItem.id);
    },
    columns() {
      if (this.searchColumns) return this.searchColumns;
      const isServiceMcr = ["MCR008", "MCR009"].includes(this.mcrType);
      const all = [
        // Kolom tombol MCR Item Note / Check Duplicate / Cancel + badge status (dirender McrItemsTable). Halaman search
        // memakai searchColumns (tanpa kolom ini), jadi tombolnya tidak pernah tampil di sana.
        { field: "", header: "", type: "note-button", style: "width: 100px; text-align: left; padding-left: 16px;" },
        { field: "material_number", header: "Mat No", style: "width: 200px; max-width: 200px", getter: (data) => materialNumberFormatter.formatMaterialNumber(data.material_number) },
        isServiceMcr ? { field: "catalog_service_category.label", header: "Serv Category", style: "width: 250px; max-width: 250px" } : { field: "catalog_material_type.label", header: "M Type", style: "width: 250px; max-width: 250px" },
        isServiceMcr ? { field: "catalog_service_group.label", header: "Serv Group", style: "width: 250px; max-width: 250px" } : { field: "catalog_material_group.label", header: "M Group", style: "width: 250px; max-width: 250px" },
        // catalog_inc = label dari BE saat item dimuat; kalau user sudah mengganti cat_inc (belum Save), label itu
        // basi -> tampil kodenya saja sampai item dimuat ulang. FE tidak lagi menulis catalog_inc.
        { field: "cat_inc", header: "Item Name", style: "width: 250px; max-width: 250px", getter: (data) => (data.catalog_inc?.inc && data.catalog_inc.inc === data.cat_inc ? data.catalog_inc.label : data.cat_inc) },
        { field: "cat_item_type", header: "Item Type", style: "width: 100px; max-width: 100px" },
        { field: "cat_short_description", header: "Short Description", style: "width: 250px; max-width: 250px" },
        { field: "catalog_uom.label", header: "UOM", style: "width: 200px; max-width: 200px" }
      ];
      // ANTAM draft / send_back: Item Name, Item Type & Short Description belum diisi (form User ANTAM = FORM02 draft),
      // diganti Specification tepat setelah UOM.
      const columns = this.isAntamUserStage
        ? all.filter((c) => !["cat_inc", "cat_item_type", "cat_short_description"].includes(c.field))
          .concat({ field: "specification", header: "Specification", style: "width: 300px; max-width: 300px" })
        : all;
      const hasMatNum = this.stateAllMcrItem.some((item) => !!item.material_number);
      return hasMatNum ? columns : columns.filter((c) => c.field !== "material_number");
    },
    // Header MCR ANTAM berstatus draft / send_back (tahap isian User). Halaman search: mcrStatus kosong -> false.
    isAntamUserStage() {
      const company = String(this.mcrHeader?.company || "").toUpperCase();
      return company === "ANTAM" && ["draft", "send_back"].includes(this.mcrStatus);
    },
    filteredMcrItems() {
      const q = (this.itemSearch || "").trim().toLowerCase();
      if (!q) return this.stateAllMcrItem;
      return this.stateAllMcrItem.filter((item) =>
        (item.specification || "").toLowerCase().includes(q) ||
        (item.cat_short_description || "").toLowerCase().includes(q) ||
        (item.material_number || "").toLowerCase().includes(q) ||
        (item.mcr_item_code || "").toLowerCase().includes(q)
      );
    }
  },
  watch: {
    activeTab(val) {
      this.activatedTabs = { ...this.activatedTabs, [val]: true };
    },
    // Tab aktif bisa hilang dari daftar (mis. mcr_type tidak relevan) -- pindah ke tab pertama yang ada.
    // `immediate`: header & config FORM02 biasanya sudah ada di store saat form dibuat, dan kalau semua kondisi tab
    // cuma membaca mcr_type/mcr_status (mis. ANTAM MCR003), daftar ini tidak pernah dihitung ulang saat item diklik
    // -> tanpa `immediate` watcher tidak pernah jalan dan activeTab tetap "0" (Basic Data yang tersembunyi).
    filteredTabList: {
      immediate: true,
      handler(list) {
        if (list.length && !list.some((tab) => tab.value === this.activeTab)) {
          this.activeTab = list[0].value;
        }
      }
    }
  },
  created() {
    // selectedMcrItem sekarang dibaca dari store (bukan data lokal yang selalu mulai null) -- buang sisa item
    // halaman sebelumnya (mis. MCR lain / halaman search) supaya form mulai tanpa item terpilih seperti dulu.
    this.$store.commit("mcr/setSelectedMcrItem", null);
  },
  mounted() {
    this.fetchViewData();
  },
  methods: {
    ...mapActions("mcr", ["storeAllMcrItem"]),
    // Aturan tombol item (diwarisi CataloguerForm.vue lama; handler di New_mcrItemActionsMixin.js).
    // Check Duplicate: item punya PO Horizontal atau SPN di Manufacture Cataloguer -- tidak di halaman search.
    hasDuplicateSource(data) {
      if (this.searchContext) return false;
      const hasLongDesc = !!(data?.cat_po_horizontal && data.cat_po_horizontal.trim());
      const hasSpn = Array.isArray(data?.cat_emcr_item_manufacturer)
        && data.cat_emcr_item_manufacturer.some((m) => m?.spn && m.spn.trim());
      return hasLongDesc || hasSpn;
    },
    // Cancel: role selain User selalu bisa; User ANTAM tidak pernah; User company lain cuma kalau MCR-nya bukan draft
    // (patokan status MCR -- dulu status item, warisan CataloguerForm lama).
    cancelVisible() {
      if (this.searchContext) return false;
      if (!this.isUserRole) return true;
      if (this.$store.state.auth.user?.company_code === "ANTAM") return false;
      return this.mcrStatus !== "draft";
    },
    async fetchViewData() {
      const { id } = this.$route.params;
      if (!id) return;

      this.mainTable.onLoad = true;
      try {
        const response = await api(this, requestListService.detailWithoutSubRelations(id), false);
        const items = (response.data?.emcr_item || []).map((item, idx) => ({ ...item, index: idx }));
        this.resetSelection();
        this.storeAllMcrItem(items);
        this.mainTable.total = items.length;
      } catch (error) {
        console.error("Failed to fetch cataloguer data", error);
        this.mainTable.total = 0;
      } finally {
        this.mainTable.onLoad = false;
      }
    },
    // Path `hydrate_from` (source_field / kolom tabel): string = satu path; array = nilai PERTAMA yang terisi, mis.
    // ["loading_group.code", "loading_group"] (relasi object atau kode string) atau nama kolom yang beda per tabel.
    resolveFirstSourcePath(source, path) {
      if (!Array.isArray(path)) return this.resolveSourcePath(source, path);
      return path.map((p) => this.resolveSourcePath(source, p)).find((v) => formConfigHelper.isFilledValue(v)) ?? null;
    },
    // Field FORM02 diisi dari response detail sesuai `hydrate_from` di config (entry pertama yang `when`-nya cocok
    // dengan item_source). Field tanpa hydrate_from dibaca dari key response yang namanya sama. Nilai dibaca dari
    // snapshot response (bukan item yang sedang diisi) supaya urutan field tidak saling memengaruhi. Baris tabel
    // di-copy dangkal supaya edit di form tidak menyentuh array response aslinya. Lihat FORM_CONFIG_SCHEMA.md "hydrate_from".
    hydrateItem(item) {
      const config = this.formConfig;
      const raw = { ...item };
      Object.keys(config?.tab_fields || {}).forEach((tab) => {
        Object.entries(formConfigHelper.getTabFieldMap(config, tab) || {}).forEach(([field, fc]) => {
          if (!fc || typeof fc !== "object" || fc.rows_from) return;
          const entry = formConfigHelper.resolveActiveHydrate(config, tab, field, this.formConfigCtx);

          if (fc.columns) {
            const rows = entry?.source_array ? this.resolveSourcePath(raw, entry.source_array) : raw[field];
            item[field] = (Array.isArray(rows) ? rows : []).map((row) => {
              const copy = { ...row };
              // `columns` opsional: { kolom_form: path_di_baris_response }, default nama sama. Path boleh array (nilai
              // PERTAMA yang terisi) atau { source_field } = path dari ITEM, bukan baris (data level item, mis. tax).
              Object.entries(entry?.columns || {}).forEach(([column, path]) => {
                copy[column] = path && typeof path === "object" && !Array.isArray(path)
                  ? this.resolveFirstSourcePath(raw, path.source_field)
                  : this.resolveFirstSourcePath(row, path);
              });
              return copy;
            });
            return;
          }

          // `source_field` array (khusus hydrate_from) = nilai PERTAMA yang terisi, mis. PO Text Cataloguer, kalau kosong
          // PO Text material yang sudah ada: ["cat_po_vertical", "eu_material.po_text_vertical"].
          if (entry?.source_field) {
            item[field] = this.resolveFirstSourcePath(raw, entry.source_field);
          }
        });
      });
      // `rows_from`: baris tabel ini = baris field lain -- ARRAY & OBJEK YANG SAMA, bukan salinan (mis. MRP = baris
      // Plant). Tambah/hapus baris di field sumber otomatis terlihat di sini dan index-nya selalu sejajar (BE memasangkan
      // cat_emcr_mrps[i] dengan cat_emcr_plants[i]). Dijalankan setelah semua field sumber terisi.
      Object.keys(config?.tab_fields || {}).forEach((tab) => {
        Object.entries(formConfigHelper.getTabFieldMap(config, tab) || {}).forEach(([field, fc]) => {
          if (!fc?.rows_from) return;
          if (!Array.isArray(item[fc.rows_from])) item[fc.rows_from] = [];
          item[field] = item[fc.rows_from];
        });
      });
      return item;
    },
    // Dipanggil toolbar Add di New_McrItem (nama method sama dengan form lain).
    addNewItem() {
      this.addItemModal.visible = true;
    },
    // Sama dengan CataloguerForm lama: buat item lewat user-form/create-from-cataloguer. Response = detail item lengkap
    // (bentuk detailEmcrItem), jadi langsung di-hydrate & dipilih tanpa fetch detail lagi.
    async addItem(fields = {}) {
      this.addItemModal.onLoading = true;
      try {
        const payload = {
          mcr_code: this.mcrHeader.mcr_code,
          material_number: fields.material_number || "",
          plant: fields.plant || "",
          mcr_type: fields.material_number ? "MCR002" : "MCR001"
        };
        // showMessage false: toast error otomatis `api` ditangani di catch (error Material Number -> $swal).
        const response = await api(this, userFormService.createFromCataloguerForm(payload), false);
        this.$toastNotifPrime("success", "Success", response?.message || "Operation completed successfully");
        const newItem = this.hydrateItem({ ...response.data });
        const items = [newItem, ...this.stateAllMcrItem];
        items.forEach((item, idx) => { item.index = idx; });
        this.storeAllMcrItem(items);
        this.mainTable.total = items.length;
        this.addItemModal.visible = false;
        // Pakai versi yang ada di store (reactive), sama seperti handleTableSelection.
        const stored = this.stateAllMcrItem.find((i) => i.mcr_item_code === newItem.mcr_item_code) || newItem;
        this.tableSelectedItem = stored;
        this.$store.commit("mcr/setSelectedMcrItem", stored);
      } catch (error) {
        console.error("Failed to add item", error);
        this.showAddItemError(error);
      } finally {
        this.addItemModal.onLoading = false;
      }
    },
    // Tombol Delete di tabel item (sama dengan CataloguerForm lama): konfirmasi -> hapus item lewat user-form/{id} -> keluarkan
    // dari daftar store; item yang sedang dipilih ikut dikosongkan.
    deleteItem(item) {
      if (!item?.id) return;
      this.confirmDialog("Are you sure you want to delete this item?", "delete it").then(async (result) => {
        if (!result.isConfirmed) return;
        try {
          await api(this, userFormService.delete(item.id), false);
          const items = this.stateAllMcrItem.filter((i) => i.id !== item.id);
          items.forEach((i, idx) => { i.index = idx; });
          if (this.tableSelectedItem?.id === item.id) this.resetSelection();
          this.storeAllMcrItem(items);
          this.mainTable.total = items.length;
          this.$toastNotifPrime("success", "Success", "Item deleted successfully");
        } catch (error) {
          console.error("Failed to delete item", error);
          this.$toastNotifPrime("error", "Error", error?.response?.data?.message || error?.message || "Something went wrong");
        }
      });
    },
    // Error Add Item dari BE: "Material Number X On Progress in: + MCR ..." (assertMaterialNumberNotRunning) -> $swal error
    // dengan pesan lengkap (awalan "Failed to create emcr items:" dibuang, baris MCR tampil per baris); error lain -> toast.
    showAddItemError(error) {
      const raw = error?.response?.data?.message;
      const message = typeof raw === "string" ? raw.replace(/^Failed to (create|update) emcr items:\s*/i, "") : "";
      if (!/^Material Number .+ On Progress in:/.test(message)) {
        this.$toastNotifPrime("error", "Error", message || raw || error?.message || "Something went wrong");
        return;
      }
      const escaped = message.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
      this.$swal({
        icon: "error",
        title: "Error",
        html: `<div style="text-align: left; white-space: pre-line;">${escaped}</div>`,
        confirmButtonText: "Close"
      });
    },
    // Dipanggil juga halaman search (lewat ref) tiap daftar hasil search diganti.
    resetSelection() {
      this.tableSelectedItem = null;
      this.$store.commit("mcr/setSelectedMcrItem", null);
    },
    async handleTableSelection(item) {
      if (!item) {
        this.resetSelection();
        return;
      }
      const source = ITEM_SOURCES[this.itemSource] || ITEM_SOURCES.emcr_item;
      const key = source.key;
      if (item[key] && item[key] === this.selectedMcrItem?.[key]) return;

      this.tabLoading = true;
      try {
        const response = await api(this, source.fetchDetail(item), false);
        const fullItem = this.hydrateItem({ ...response.data, index: item.index });
        this.storeAllMcrItem(this.stateAllMcrItem.map((i) => (i[key] === fullItem[key] ? fullItem : i)));
        // Pakai versi yang ada di store (reactive) supaya edit di tab langsung tercermin di state item.
        const stored = this.stateAllMcrItem.find((i) => i[key] === fullItem[key]) || fullItem;
        this.tableSelectedItem = stored;
        this.$store.commit("mcr/setSelectedMcrItem", stored);
      } catch (error) {
        console.error("Failed to fetch item detail", error);
      } finally {
        this.tabLoading = false;
      }
    }
  }
};
</script>

<style scoped>
  .cataloguer-form-v2 {
    padding: 0 0.25rem 2rem;
  }

  /* Card body height - all tabs */
  :deep(.card-body-tab) {
    min-height: 480px;
    max-height: 480px;
    overflow-y: auto;
    overflow-x: visible;
  }

  /* Custom spacing between form rows */
  .form-row-spacing {
    margin-bottom: 0.6rem;
  }

  /* Override disabled input background color to match legacy */
  :deep(input:disabled),
  :deep(textarea:disabled),
  :deep(.p-select:disabled),
  :deep(.p-disabled) {
    background-color: #f8f9fc !important;
  }

  /* Value text lebih tebal daripada placeholder, biar kebedaan mana yang sudah keisi */
  :deep(input),
  :deep(textarea),
  :deep(.p-select-label) {
    font-weight: 600;
  }

  :deep(input::placeholder),
  :deep(textarea::placeholder) {
    font-weight: 400;
  }

  :deep(.p-select-label.p-placeholder) {
    font-weight: 400;
  }

  /* Force small text in DataTable body only */
  :deep(.p-datatable-sm .p-datatable-tbody > tr > td) {
    font-size: 0.875rem !important;
  }

  /* Centered column header */
  :deep(.centered-column-header) {
    text-align: center;
    width: 100%;
  }

  /* Center align column headers */
  :deep(.p-datatable-thead > tr > th.text-center),
  :deep(.p-datatable-thead > tr > th.bg-light.text-center),
  :deep(.p-datatable-thead > tr > th.text-center .p-column-header-content),
  :deep(.p-datatable-thead > tr > th.bg-light.text-center .p-column-header-content) {
    text-align: center !important;
    justify-content: center !important;
  }

  :deep(.p-datatable .p-datatable-thead > tr > th) {
    white-space: nowrap !important;
  }

  :deep(.p-datatable .p-datatable-tbody > tr > td) {
    padding: 9px 0.75rem !important;
  }

  :deep(.p-datatable .p-datatable-tbody > tr.p-datatable-empty-message > td) {
    border: none !important;
    padding: 0 !important;
  }

  :deep(.p-datatable .p-datatable-tbody > tr > td span) {
    font-size: 12px !important;
  }

  /* Pengecualian: value/placeholder SelectPrime di tabel tab (mis. New_PlantStorageTab) juga berupa <span>
     (template #value) -- ikut ukuran .p-select-label (0.875rem) supaya sama dengan field di luar tabel. */
  :deep(.p-datatable .p-datatable-tbody > tr > td .p-select-label span) {
    font-size: inherit !important;
  }

  /* Validation */
  :deep(.p-invalid) {
    border-color: #dc3545 !important;
  }
  :deep(.p-invalid:focus) {
    border-color: #dc3545 !important;
    box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25) !important;
  }
  :deep(.p-inputnumber.p-invalid .p-inputnumber-input) {
    border-color: #dc3545 !important;
  }
  :deep(.p-invalid::placeholder),
  :deep(.p-invalid .p-inputtext::placeholder),
  :deep(.p-invalid .p-autocomplete-input::placeholder),
  :deep(.p-inputnumber.p-invalid .p-inputnumber-input::placeholder) {
    color: #6c757d !important;
    opacity: 0.6 !important;
  }

  /* Remove tab panel left/right padding so cards align with tab titles */
  :deep(.p-tabpanels) {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }

  :deep(.p-tabpanel) {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }

  /* Tabs */
  :deep(.p-tab) {
    font-size: 11px !important;
    padding: 10px 14px !important;
    text-transform: uppercase;
    color: #6b7280;
    font-weight: 500;
    border-bottom: 2px solid transparent;
    transition: color 0.2s, background 0.2s, border-color 0.2s;
    white-space: nowrap;
  }

  :deep(.p-tab:hover:not([data-p-active="true"])) {
    color: #1e2d6e !important;
    background-color: #f0f4ff !important;
    border-bottom-color: #a5b4d8 !important;
  }

  :deep(.p-tab[data-p-active="true"]) {
    color: #1e2d6e !important;
    font-weight: 700 !important;
    background: linear-gradient(180deg, #eef2ff 0%, #ffffff 100%) !important;
    border-bottom: 2px solid #1e2d6e !important;
  }

  :deep(.p-tablist-prev-button) {
    color: #1e2d6e;
    background: #f8f9fc;
    border: none;
    transition: background 0.2s;
  }

  :deep(.p-tablist-next-button) {
    display: none !important;
  }

  :deep(.p-tablist) {
    padding-right: 36px;
  }

  .tabs-container {
    position: relative;
  }

  .tab-skeleton-overlay {
    padding: 0 0 1rem;
  }

  .tabs-more-trigger {
    position: absolute;
    right: 0;
    top: 0;
    height: 42px;
    width: 2.5rem;
    cursor: pointer;
    z-index: 5;
    background: #f8f9fc;
    color: #64748b;
    box-shadow: none;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .tabs-dropdown-menu {
    position: absolute;
    right: 0;
    top: 42px;
    z-index: 1000;
    background: #ffffff;
    border: 1px solid #dee2e6;
    border-radius: 6px;
    box-shadow: 0 4px 16px rgba(30, 45, 110, 0.12);
    min-width: 220px;
    overflow: hidden;
  }

  .tabs-dropdown-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 14px;
    font-size: 11px;
    text-transform: uppercase;
    font-weight: 500;
    color: #374151;
    cursor: pointer;
    border-bottom: 1px solid #f3f4f6;
  }

  .tabs-dropdown-item:last-child {
    border-bottom: none;
  }

  .tabs-dropdown-item:hover {
    background: #f0f4ff;
    color: #1e2d6e;
  }

  .tabs-dropdown-item.is-active {
    background: #eef2ff;
    color: #1e2d6e;
    font-weight: 700;
    border-left: 3px solid #1e2d6e;
    padding-left: 11px;
  }

  .tabs-dropdown-error {
    color: red;
    font-weight: bold;
    font-size: 12px;
    margin-left: 6px;
  }

  .tabs-dropdown-enter-active,
  .tabs-dropdown-leave-active {
    transition: opacity 0.15s, transform 0.15s;
  }

  .tabs-dropdown-enter-from,
  .tabs-dropdown-leave-to {
    opacity: 0;
    transform: translateY(-6px);
  }

  /* Error indicator */
  .p-tablist .exclamation-mark-error-tab {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 14px;
    height: 14px;
    padding: 0 3px;
    background-color: #dc3545;
    color: white !important;
    font-weight: bold !important;
    font-size: 9px;
    border-radius: 3px;
    margin-left: 4px;
    vertical-align: middle;
  }

  :deep(.exclamation-mark-error-tab) {
    color: red !important;
    font-weight: bold !important;
  }

  /* Selected row highlight */
  :deep(.selected-row) {
    background-color: #FFE391 !important;
    color: rgb(22, 19, 19) !important;
  }
  /* Read-only fields: background abu-abu + tidak bisa diklik, tapi teks tetap gelap/jelas dibaca.
     Ditarget dari state disabled elemen itu sendiri (:disabled / .p-disabled), BUKAN dari ancestor
     .form-disabled — soalnya field bisa disabled karena dua sumber beda: (1) seluruh tab/form
     read-only (.form-disabled kepasang), atau (2) per-field lewat isEnabledFilled() doang, di mana
     .form-disabled TIDAK pernah kepasang. Kalau selector-nya gantung ke .form-disabled, kasus (2)
     tidak pernah ke-cover dan jatuh balik ke styling native PrimeVue apa adanya (makanya beda-beda).
     Input native Checkbox/RadioButton PrimeVue (.p-checkbox-input/.p-radiobutton-input) DIKECUALIKAN: elemen itu transparan
     & menutupi kotak centang (absolute, z-index 1) -- kalau ikut opacity 1 + background abu-abu, ikon centang tertutup dan
     checkbox disabled yang bernilai "X" tampak kosong. */
  :deep(input:disabled:not(.p-checkbox-input):not(.p-radiobutton-input)),
  :deep(select:disabled),
  :deep(textarea:disabled),
  :deep(.p-dropdown.p-disabled),
  :deep(.p-select.p-disabled),
  :deep(.p-inputnumber.p-disabled),
  :deep(.p-autocomplete.p-disabled),
  :deep(.p-calendar.p-disabled),
  :deep(.p-checkbox.p-disabled) {
    cursor: not-allowed !important;
    opacity: 1 !important;
    background-color: #f5f5f5 !important;
    border-color: #ced4da !important;
    color: #585f67 !important;
  }
  /* pointer-events: none; */

  /* Select menampilkan value lewat <span class="p-select-label">, tidak ke-cover selector di
     atas. Dikecualikan saat placeholder (.p-placeholder) supaya field kosong tetap kelihatan beda. */
  :deep(.p-select.p-disabled .p-select-label:not(.p-placeholder)) {
    color: #585f67 !important;
  }

  /* Keep table headers visible */
  .form-disabled :deep(thead),
  .form-disabled :deep(.p-datatable-thead) {
    opacity: 1 !important;
  }
</style>

<style>
  .multiselect-small-panel .p-multiselect-option {
    padding: 0.2rem 0.5rem !important;
    font-size: 0.7rem !important;
  }
  .multiselect-small-panel .p-multiselect-option span {
    font-size: 0.7rem !important;
  }
  .multiselect-small-panel .p-multiselect-header {
    padding: 0.3rem 0.5rem 0.2rem 0.7rem !important;
  }
</style>

<style scoped>
::v-deep .p-select:not(.p-select-editable) {
  min-height: 38px !important;
  height: 38px !important;
}
::v-deep .p-select-sm:not(.p-select-editable) .p-select-label,
::v-deep .p-select:not(.p-select-editable) .p-select-label {
  font-size: 0.875rem !important;
}
</style>
